import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { motion, AnimatePresence } from "framer-motion"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { CREATE_ITEM, CREATE_LIST } from "constants/index"
import { NAVIGATE_TO_CREATE_ITEM_PAGE } from "constants/events"
import { PathInfo } from "types/general.types"
import MENUS from "constants/pages"
import { PATH_INFOS } from "utils/navigatorUtils"
import { useAppSelector } from "appRedux/hooks"
import { resetListFormSlice } from "appRedux/slices/listFormSlice"
import { resetExploreSlice } from "appRedux/slices/exploreSlice"
import { resetFeedSlice } from "appRedux/slices/feedSlice"
import { feedApi } from "appRedux/slices/feedApiSlice"
import { useAuth } from "context/AuthContext"
import ItemIcon from "components/atoms/_icons/ItemIcon"
import ListIcon from "components/atoms/_icons/ListIcon"
import SpeedDial from "components/atoms/SpeedDial"

export default memo(function Navigation() {
    const [showFabModalContent, setShowFabModalContent] = useState(false)

    const unseenNotificationsCount = useAppSelector((state) => state.notification.unseenCount)

    const { isAuthenticated, user } = useAuth()

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const location = useLocation()
    const pathname = location.pathname
    const currentPathname = location.pathname

    const actions = [
      {
        key: CREATE_ITEM,
        icon: <ItemIcon/>,
        name: t("items.addNewItem"),
        onClick: handleCreateNewItem
      },
      {
        key: CREATE_LIST,
        icon: <ListIcon/>,
        name: t("collections.addNewList"),
        onClick: handleCreateNewList
      }
    ]

    useEffect(() => {
      if (showFabModalContent) {
        setShowFabModalContent(false)
      }
    }, [pathname])

    const menusToRenderInNavigation = useMemo(() => {
      const menusToRender: { [key: string]: PathInfo } = {}
      Object.keys(PATH_INFOS).forEach((menu: string) => {
        if (PATH_INFOS[menu as keyof typeof MENUS].showInNavigation) {
          menusToRender[menu] = PATH_INFOS[menu as keyof typeof PATH_INFOS]
        }
      })
      return menusToRender
    }, [])

    const renderMenuItems = useCallback((side: "left" | "right") => {
      const sortedMenus = Object.entries(menusToRenderInNavigation).sort((a: [string, PathInfo], b: [string, PathInfo]) => a[1].orderInNavigation < b[1].orderInNavigation ? -1 : 1)
      let menusToRender: [string, PathInfo][] = []
      if (side === "left") {
        menusToRender = sortedMenus.slice(0, sortedMenus.length / 2)
      } else if (side === "right") {
        menusToRender = sortedMenus.slice(sortedMenus.length / 2)
      }

      return menusToRender.map(([key, menu]) => (
        <MenuItem
          key={key}
          onClick={() => handleNavigationButtonClicked(menu.path)}
        >
          {isPageSelected(menu.path) ? menu.selectedIcon : menu.icon}
          <AnimatePresence>
            {(key === PATH_INFOS.notifications.key && unseenNotificationsCount > 0) &&
                <motion.div
                    key="notification-count"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <UnseenNotificationCount>{unseenNotificationsCount}</UnseenNotificationCount>
                </motion.div>
            }
          </AnimatePresence>
        </MenuItem>
      ))
    }, [menusToRenderInNavigation, isPageSelected, unseenNotificationsCount])

    function handleCreateNewItem() {
      document.dispatchEvent(new CustomEvent(NAVIGATE_TO_CREATE_ITEM_PAGE))
    }

    function handleCreateNewList() {
      dispatch(resetListFormSlice())
      navigate("/list")
    }

    function isPageSelected(pagePath: string) {
      const pagePathChunks = pagePath.replace("/", "").split("/")
      const currentPathnameChunks = currentPathname.replace("/", "").split("/")
      let isSelected = false
      for (let i = 0; i < pagePathChunks.length; i++) {
        if (pagePathChunks[i].startsWith(":")) {
          return isSelected
        }
        isSelected = pagePathChunks[i] === currentPathnameChunks[i]
      }
      return isSelected
    }

    function handleNavigationButtonClicked(path: string) {
      if (currentPathname === path) return
      if (currentPathname === PATH_INFOS.explore.path && path !== PATH_INFOS.explore.path) dispatch(resetExploreSlice())
      if (currentPathname === PATH_INFOS.home.path && path !== PATH_INFOS.home.path) {
        dispatch(resetFeedSlice())
        dispatch(feedApi.util.resetApiState())
      }

      window.scrollTo({ top: 0, left: 0, behavior: "auto" })
      navigateToPath(path)
    }

    function navigateToPath(path: string) {
      if (path.endsWith("/:handle")) {
        if (!isAuthenticated || !user) return
        path = path.replace(":handle", user.handle)
      }
      navigate(path)
    }

    function toggleShowFabModalContent() {
      setShowFabModalContent(!showFabModalContent)
    }

    return (
      <NavigationContainer id="navigation-container">
        <NavigationItemsWrapper>
          <NavigationItems>
            {renderMenuItems("left")}
            <FabWrapper>
              <SpeedDial
                actions={actions}
                open={showFabModalContent}
                onClose={toggleShowFabModalContent}
              />
            </FabWrapper>
            {renderMenuItems("right")}
          </NavigationItems>
        </NavigationItemsWrapper>
        <FreeSpace/>
      </NavigationContainer>
    )
  }
)

const NavigationContainer = styled(Stack)(({ theme }) => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  position: "fixed",
  bottom: 0,
  boxShadow: "0 0 10px 0 #F1F1F1",
  zIndex: 10
}))

const NavigationItemsWrapper = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  width: "100%",
  height: "58px"
}))

const NavigationItems = styled(Stack)(({ theme }) => ({
  width: "100%",
  bottom: 0,
  flexDirection: "row-reverse",
  justifyContent: "space-between",
  alignItems: "flex-end",
  backgroundColor: "#FFFFFF",
  padding: "6px 24px",
  borderRadius: "16px 16px 0 0"
}))

const MenuItem = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: "10px",
  borderRadius: "8px",
  zIndex: 10,
  position: "relative"
}))

const UnseenNotificationCount = styled(Stack)(({ theme }) => ({
  height: "16px",
  padding: "0 5px",
  justifyContent: "center",
  alignItems: "center",
  position: "absolute",
  top: "4px",
  left: "6px",
  borderRadius: "50%",
  backgroundColor: theme.palette.error["4"],
  color: "#FFFFFF",
  fontSize: "1rem",
  fontFamily: "VazirmatnFD, serif !important"
}))

const FabWrapper = styled("div")(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: "50px 50px 0 0",
  width: "90px",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-end",
  boxShadow: "0 0 10px 0 #F1F1F1",
  paddingTop: "6px",

  "&::before": {
    content: "''",
    width: "100%",
    height: "76px",
    backgroundColor: "#FFFFFF",
    position: "absolute",
    bottom: 0,
    right: 0,
    borderRadius: "16px 16px 0 0"
  }
}))

const FreeSpace = styled("div")(({ theme }) => ({
  width: "100%",
  height: "18px"
}))
